<template>
  <div :class="$style.container">
    <div :class="$pgCTStyle.headerContainer">
      <div :class="$pgCTStyle.title">{{ computedTitle }}</div>

      <div :class="$pgCTStyle.headerRightCTAs">
        <PGHeaderActionButton
          v-if="showAllGroupsCTA"
          :text="$t('all-groups')"
          :clickFunction="openAllGroupsLeaderboard"
          backBg="#000000"
          frontBg="#676767"
          textColor="#ffc642"
          :button-width="isMobile ? '70px' : isTablet ? '72px' : '86px'"
        />

        <div
          v-if="!isGroupComponent"
          :title="$t('score-criteria')"
          :class="$pgCTStyle.actionIconContainer"
          @click="openCalculationModal"
        >
          <img :class="$pgCTStyle.actionIcon" :src="calculatorIcon" alt="calculate" />
        </div>
      </div>
    </div>

    <div :class="$style.bodyContainer">
      <div
        :class="$style.textContainer"
        v-for="(player, index) in sortedPlayers"
        :key="player.id"
      >
        <div :class="$style.textTopContainer" @click="toggleExpandPlayer(player.id)">
          <div :class="$style.textTopLeftContainer">
            <div
              :class="[
                $style.playerRank,
                index + 1 == 1
                  ? $style.goldRank
                  : index + 1 == 2
                  ? $style.silverRank
                  : index + 1 == 3
                  ? $style.bronzeRank
                  : '',
              ]"
            >
              {{ index + 1 }}
            </div>

            <img
              :class="$style.playerAvatar"
              :src="player.avatar ? player.avatar : defaultAvatar"
              alt="player"
            />

            <div :class="$style.playerName">
              {{ player.username ? player.username : "Player" }}
            </div>
          </div>

          <div :class="$style.textTopRightContainer">
            <div :class="$style.playerScore">{{ player?.score ?? "--" }}</div>

            <div
              :class="$style.dropDownIconContainer"
              @click.stop="toggleExpandPlayer(player.id)"
            >
              <img
                :src="dropDownIcon"
                alt="icon"
                :class="[
                  $style.dropDownIcon,
                  expandStates[player.id] ? $style.iconRotated : '',
                ]"
              />
            </div>
          </div>
        </div>

        <transition
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <div v-if="expandStates[player.id]" :class="$style.textBottomContainer">
            <template v-if="(player.breakdown || []).length">
              <div
                v-for="(item, i) in player.breakdown"
                :key="i"
                :class="$style.breakdownRow"
              >
                <div :class="$style.breakdownLabel">{{ item.label }}</div>
                <div :class="$style.breakdownPoints">+{{ item.points }}</div>
              </div>
            </template>

          <template v-else>
            <div :class="$style.noBreakdownText">
              {{
                Number(player?.score || 0) === 0
                  ? $t("no-points-earned-this-game")
                  : $t("no-score-breakdown-available")
              }}
            </div>
          </template>
          </div>
        </transition>
      </div>
    </div>

    <CalculationModal
      v-if="showCalculationModal"
      :closeModal="closeCalculationModal"
    />
  </div>
</template>

<script>
import styles from "@/css/LeaderboardBox.module.css";
import playGameComponentsTemplateStyles from "@/css/PlayGameComponentsTemplate.module.css";
import globalStyles from "@/css/global.module.css";
import CalculatorIcon from "@/assets/icons/calculator.svg";
import { defaultAvatar } from "@/utils/data";
import CalculationModal from "./CalculationModal.vue";
import { NOOP } from "@/utils/functions";
import DropDownIcon from "@/assets/icons/down-black.svg";
// import UtilButton from "../util/UtilButton.vue";
import PGHeaderActionButton from "../buttons/PGHeaderActionButton.vue";
import useDeviceType from "@/utils/useDeviceType";

const device = useDeviceType();

export default {
  components: { CalculationModal, PGHeaderActionButton },
  name: "LeaderboardBox",
  props: {
  canOpenAllGroups: {
    type: Boolean,
    required: false,
    default: false,
  },
    sortedPlayers: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    openAllGroupsLeaderboard: {
      type: Function,
      required: false,
      default: NOOP,
    },
    isHost: {
      type: Boolean,
      required: true,
    },
    isGroupComponent: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      defaultAvatar: defaultAvatar,
      calculatorIcon: CalculatorIcon,
      showCalculationModal: false,
      dropDownIcon: DropDownIcon,
      expandStates: {},
    };
  },
  computed: {
    $style() {
      return styles;
    },
    $globalStyle() {
      return globalStyles;
    },
    $pgCTStyle() {
      return playGameComponentsTemplateStyles;
    },
    computedTitle() {
      return this.title || this.$t("players-scores");
    },
    width() {
      return device.width.value;
    },
    height() {
      return device.height.value;
    },
    isMobile() {
      return device.isMobile.value;
    },
    isTablet() {
      return device.isTablet.value;
    },
    isDesktop() {
      return device.isDesktop.value;
    },
    // sortedPlayers() {
    //   return [...this.players].sort((a, b) => {
    //     const scoreA = typeof a.score === "number" ? a.score : -Infinity; // Treat null/empty as lowest
    //     const scoreB = typeof b.score === "number" ? b.score : -Infinity;
    //     return scoreB - scoreA; // Descending order: highest score first
    //   });
    // },
    showAllGroupsCTA() {
      return this.openAllGroupsLeaderboard !== NOOP && this.canOpenAllGroups;
    },
  },
  methods: {
    closeCalculationModal() {
      this.showCalculationModal = false;
    },
    openCalculationModal() {
      this.showCalculationModal = true;
    },
    toggleExpandPlayer(playerId) {
      this.expandStates[playerId] = !this.expandStates[playerId];
    },

    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    enter(el, done) {
      const height = el.scrollHeight;
      el.style.transition = "height 0.3s ease, opacity 0.3s ease";
      el.style.height = height + "px";
      el.style.opacity = "1";
      setTimeout(() => {
        el.style.height = "auto";
        done();
      }, 300);
    },
    afterEnter(el) {
      el.style.height = "auto";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    leave(el, done) {
      el.style.transition = "height 0.3s ease, opacity 0.3s ease";
      el.style.height = "0";
      el.style.opacity = "0";
      setTimeout(() => done(), 300);
    },
    afterLeave(el) {
      el.style.height = "auto";
    },
  },
mounted() {},
  beforeUnmount() {},
  watch: {
    sortedPlayers: {
      deep: true,
      immediate: true,
      handler(newVal) {

        (newVal || []).forEach((p) => {
          if (this.expandStates[p.id] === undefined) {
            this.expandStates[p.id] = false;
          }
        });
      },
    },
  },
};
</script>

<style scoped></style>
